import { OverlayPanel } from 'primereact/overlaypanel';
import { useRef } from 'react';

import { useToast } from '@/components/hooks/useToast';
import { PermissionDeniedMessages } from '@/components/providers/PermissionsContextProvider/Permissions.types';
import { usePermissionGate } from '@/components/providers/PermissionsContextProvider/usePermissionGate';
import {
    useAddConditionWatcherMutation,
    useRemoveConditionWatcherMutation,
} from '@/components/shared/api/mutations/conditions';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { Feature } from '@/components/shared/constants/features';
import { PageRoutes } from '@/components/shared/routes';

import {
    ActionMenuBody,
    ActionMenuItem,
    ActionMenuItemDanger,
    ActionMenuText,
    ConditionActionButton,
} from './ApplicantConditions.StyledComponents';

interface ConditionActionMenuProps {
    applicantId: string;
    conditionId: string;
    isWatching?: boolean;
    canDelete?: boolean;
    canEdit?: boolean;
    onDelete?: () => void;
    onEdit?: () => void;
    onAddFollowUp?: () => void;
}

const buildConditionLink = (applicantId: string, conditionId: string) => {
    const path = PageRoutes.APPLICANT.replace(':applicantId', encodeURIComponent(applicantId));
    const query = new URLSearchParams({ tab: 'conditions', conditionId });
    return `${window.location.origin}${path}?${query.toString()}`;
};

export const ConditionActionMenu = ({
    applicantId,
    conditionId,
    isWatching,
    canDelete,
    canEdit,
    onDelete,
    onEdit,
    onAddFollowUp,
}: ConditionActionMenuProps) => {
    const overlayRef = useRef<OverlayPanel>(null);
    const addWatcher = useAddConditionWatcherMutation();
    const removeWatcher = useRemoveConditionWatcherMutation();
    const { isConditionsWatcherEnabled } = useFeatureFlags();

    const deleteGate = usePermissionGate(
        Feature.ProtectedConditions,
        PermissionDeniedMessages.DeleteCondition,
        canDelete,
    );

    const editGate = usePermissionGate(
        Feature.ProtectedConditions,
        PermissionDeniedMessages.EditCondition,
        canEdit,
    );

    const { toast, showToast } = useToast(`condition-link-toast-${conditionId}`);
    const isIframed = window.self !== window.top;
    const conditionLink = buildConditionLink(applicantId, conditionId);

    const closeMenu = () => overlayRef.current?.hide();
    const isWatchingPending = addWatcher.isPending || removeWatcher.isPending;

    const handleToggleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        overlayRef.current?.toggle(e);
    };

    const handleCopyLink = () => {
        closeMenu();
        navigator.clipboard.writeText(conditionLink).then(
            () => showToast('success', 'Link Copied to Clipboard'),
            () => showToast('error', 'Unable to copy link'),
        );
    };

    const handleToggleWatching = () => {
        closeMenu();
        const request = { applicantId, conditionId };
        if (isWatching) {
            removeWatcher.mutate(request);
        } else {
            addWatcher.mutate(request);
        }
    };

    const handleDeleteClick = deleteGate.gateAction(() => {
        closeMenu();
        onDelete?.();
    });

    const handleAddFollowUpClick = () => {
        closeMenu();
        onAddFollowUp?.();
    };

    const handleEditClick = editGate.gateAction(() => {
        closeMenu();
        onEdit?.();
    });

    return (
        <>
            <ConditionActionButton
                type="button"
                onClick={handleToggleClick}
                aria-label="Task/condition actions"
                data-condition-action-slot=""
                data-testid={`condition-actions-${conditionId}`}
            >
                <i className="pi pi-cog" aria-hidden />
            </ConditionActionButton>
            <OverlayPanel ref={overlayRef} data-testid={`condition-actions-menu-${conditionId}`}>
                <ActionMenuBody>
                    {isIframed ? (
                        <ActionMenuText data-testid={`condition-link-text-${conditionId}`}>
                            {conditionLink}
                        </ActionMenuText>
                    ) : (
                        <ActionMenuItem
                            type="button"
                            onClick={handleCopyLink}
                            data-testid={`condition-copy-link-${conditionId}`}
                        >
                            <i className="pi pi-link" aria-hidden />
                            Copy link to this T&amp;C
                        </ActionMenuItem>
                    )}
                    {onAddFollowUp ? (
                        <ActionMenuItem
                            type="button"
                            onClick={handleAddFollowUpClick}
                            data-testid={`condition-add-follow-up-${conditionId}`}
                        >
                            <i className="pi pi-plus" aria-hidden />
                            Add Follow-Up Task/Condition
                        </ActionMenuItem>
                    ) : null}
                    {onEdit ? (
                        <ActionMenuItem
                            type="button"
                            onClick={handleEditClick}
                            aria-disabled={editGate.disabled || undefined}
                            title={editGate.title}
                            data-testid={`condition-edit-${conditionId}`}
                        >
                            <i className="pi pi-pencil" aria-hidden />
                            Edit Task/Condition
                        </ActionMenuItem>
                    ) : null}
                    {isConditionsWatcherEnabled ? (
                        <ActionMenuItem
                            type="button"
                            onClick={handleToggleWatching}
                            disabled={isWatchingPending}
                            data-testid={`condition-toggle-watch-${conditionId}`}
                        >
                            <i
                                className={isWatching ? 'pi pi-eye-slash' : 'pi pi-eye'}
                                aria-hidden
                            />
                            {isWatching ? 'Stop Watching' : 'Start Watching'}
                        </ActionMenuItem>
                    ) : null}
                    {onDelete ? (
                        <ActionMenuItemDanger
                            type="button"
                            onClick={handleDeleteClick}
                            aria-disabled={deleteGate.disabled || undefined}
                            title={deleteGate.title}
                            data-testid={`condition-delete-${conditionId}`}
                        >
                            <i className="pi pi-trash" aria-hidden />
                            Delete Task/Condition
                        </ActionMenuItemDanger>
                    ) : null}
                </ActionMenuBody>
            </OverlayPanel>
            {toast}
        </>
    );
};
